import React, { useEffect, useRef } from 'react';

const COUNT = 64;
const PERCEPTION = 70;   // neighbour radius for flocking rules
const LINK_DIST = 90;    // draw connecting lines under this distance
const MIN_SPEED = 30;
const MAX_SPEED = 70;
const EDGE_MARGIN = 60;  // soft steer-back zone at the borders

/** Ambient 2D boids with plexus-style lines, drawn on a canvas that fills
 *  its container. Pauses off-screen. */
const LabsFlock = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let dpr = 1;
    let raf = null;
    let running = true;
    let last = performance.now();

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    resize();

    const boids = Array.from({ length: COUNT }, () => ({
      x: Math.random() * (width || 600),
      y: Math.random() * (height || 600),
      vx: (Math.random() * 2 - 1) * MAX_SPEED,
      vy: (Math.random() * 2 - 1) * MAX_SPEED,
    }));

    const step = (dt) => {
      for (let i = 0; i < boids.length; i++) {
        const b = boids[i];
        let ax = 0;
        let ay = 0;
        let cohereX = 0;
        let cohereY = 0;
        let alignX = 0;
        let alignY = 0;
        let neighbours = 0;
        for (let j = 0; j < boids.length; j++) {
          if (i === j) continue;
          const o = boids[j];
          const dx = o.x - b.x;
          const dy = o.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 === 0 || d2 > PERCEPTION * PERCEPTION) continue;
          const d = Math.sqrt(d2);
          neighbours++;
          cohereX += o.x;
          cohereY += o.y;
          alignX += o.vx;
          alignY += o.vy;
          const push = (PERCEPTION - d) / PERCEPTION;
          ax -= (dx / d) * push * 120;
          ay -= (dy / d) * push * 120;
        }
        if (neighbours) {
          ax += (cohereX / neighbours - b.x) * 0.6;
          ay += (cohereY / neighbours - b.y) * 0.6;
          ax += (alignX / neighbours - b.vx) * 1.4;
          ay += (alignY / neighbours - b.vy) * 1.4;
        }
        if (b.x < EDGE_MARGIN) ax += (EDGE_MARGIN - b.x) * 2.4;
        if (b.y < EDGE_MARGIN) ay += (EDGE_MARGIN - b.y) * 2.4;
        if (b.x > width - EDGE_MARGIN) ax -= (b.x - (width - EDGE_MARGIN)) * 2.4;
        if (b.y > height - EDGE_MARGIN) ay -= (b.y - (height - EDGE_MARGIN)) * 2.4;

        b.vx += ax * dt;
        b.vy += ay * dt;
        const speed = Math.hypot(b.vx, b.vy) || 1;
        const clamped = Math.max(MIN_SPEED, Math.min(MAX_SPEED, speed));
        b.vx = (b.vx / speed) * clamped;
        b.vy = (b.vy / speed) * clamped;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
      }
    };

    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#14161c';
      ctx.fillRect(0, 0, width, height);
      for (let i = 0; i < boids.length; i++) {
        for (let j = i + 1; j < boids.length; j++) {
          const dx = boids[j].x - boids[i].x;
          const dy = boids[j].y - boids[i].y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LINK_DIST * LINK_DIST) continue;
          const alpha = (1 - Math.sqrt(d2) / LINK_DIST) * 0.35;
          ctx.strokeStyle = `rgba(205, 211, 224, ${alpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(boids[i].x, boids[i].y);
          ctx.lineTo(boids[j].x, boids[j].y);
          ctx.stroke();
        }
      }
      // The first two carry the Flow travellers' colours
      boids.forEach((b, index) => {
        ctx.fillStyle = index === 0 ? '#ff6b57' : index === 1 ? '#2fbfae' : '#cdd3e0';
        ctx.beginPath();
        ctx.arc(b.x, b.y, index < 2 ? 3.5 : 2, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!running || !width || !height) return;
      step(dt);
      draw();
    };
    raf = requestAnimationFrame(frame);

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        running = entry.isIntersecting;
        last = performance.now();
      },
      { threshold: 0.05 }
    );
    visibilityObserver.observe(canvas);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="labs-flock-canvas" aria-hidden="true" />;
};

export default LabsFlock;
